import { GraphQLClient } from 'graphql-request';

import { trackEvent, trackInteraction } from '@/lib/event_tracker';
import { ensureImmutable } from './data/utils';
import { trackError } from '@/lib/error_tracker';
import { isPresent, isServer } from 'shared/lib/utils';

export const BACKEND_API_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL;
export const SESSION_COOKIE_NAME = process.env.NEXT_PUBLIC_SESSION_COOKIE_NAME;

function makeFetchOptions(
  isJsonRequest: boolean,
  sessionId: string,
  extraHeaders?
) {
  const options: { credentials: RequestCredentials; headers: Headers } = {
    credentials: 'include',
    headers: {
      ...(isJsonRequest && {
        'Content-Type': 'application/json',
        ...extraHeaders,
        ...(isPresent(sessionId) &&
          // NOTE: the browser's `fetch` doesn't allow the cookie header to be
          // set, while node's `fetch` requires it.
          isServer() && {
            Cookie: `${SESSION_COOKIE_NAME}=${sessionId}`,
          }),
      }),
    },
  };

  return options;
}

export function graphqlClient(sessionDataOrSession, paramsAndSearchParams?) {
  const { searchParams } = paramsAndSearchParams || {};
  const session = ensureImmutable(sessionDataOrSession);

  let xContextualizeAiCodeHeader;

  if (searchParams?.code) {
    xContextualizeAiCodeHeader = {
      'X-Contextualize-Ai-Code': searchParams.code,
    };
  }

  return new GraphQLClient(
    `${BACKEND_API_URL}/graph`,
    makeFetchOptions(
      true,
      session.getIn(['session', 'id']),
      xContextualizeAiCodeHeader
    )
  );
}

function makeFullErrorMessage(error, errorEventId) {
  const errorEventIdPart = errorEventId ? (
    <p>Error ID: {errorEventId}</p>
  ) : null;

  return (
    <div className="flex flex-col gap-4">
      <p>
        {error.errorType}: {error.errorMessage}
      </p>
      {errorEventIdPart}
    </div>
  );
}

export async function requestGraphql(
  sessionOrSessionData,
  graphql,
  pathname,
  query,
  queryDataField,
  variables,
  eventName,
  eventContext,
  options?
) {
  if (!options?.skipEventTracking) {
    trackInteraction(
      sessionOrSessionData,
      pathname,
      `${eventName} Attempted`,
      eventContext
    );
  }

  try {
    const response = await graphql.request(query, variables);

    const data = response[queryDataField];

    if (data === undefined) {
      const error = {
        errorType: 'response_body_field_missing',
        errorMessage: `Was expecting the field "${queryDataField}" in the response`,
        query: queryDataField,
        pathname,
        ...eventContext,
      };

      trackError(
        sessionOrSessionData,
        `Response Body Field Missing`,
        null,
        error
      );

      return {
        error: {
          ...error,
          fullErrorMessage: (
            <p>
              {error.errorType}: {error.errorMessage}
            </p>
          ),
        },
      };
    }

    if (data.typename?.endsWith('_error')) {
      const error = {
        errorType: data.typename,
        errorMessage: data.message,
        query: queryDataField,
        pathname,
        ...eventContext,
      };

      if (data.typename?.endsWith('_not_found_error')) {
        trackError(sessionOrSessionData, `Page Not Found`, null, error);

        return {
          error: {
            ...error,
            fullErrorMessage: (
              <p>
                {error.errorType}: {error.errorMessage}
              </p>
            ),
          },
        };
      }

      const errorEventId = trackError(
        sessionOrSessionData,
        `${eventName} Failed`,
        null,
        error
      );

      return {
        error: {
          ...error,
          errorEventId,
          fullErrorMessage: makeFullErrorMessage(error, errorEventId),
        },
      };
    } else {
      if (!options?.skipEventTracking) {
        trackEvent(
          sessionOrSessionData,
          pathname,
          `${eventName} Succeeded`,
          eventContext
        );
      }

      return { data };
    }
  } catch (exception) {
    let errorType;
    let errorMessage;

    if (exception.response) {
      if (exception.response.status && exception.response.status) {
        errorType = `Server ${exception.response.status}`;
      } else {
        errorType = 'Server Error';
      }

      if (exception.response.error) {
        errorMessage = exception.response.error;
      } else if (exception.response.errors) {
        errorMessage = exception.response.errors
          .map((error) => {
            let context = '';

            if (error.extensions) {
              if (error.extensions.code) {
                context = ` (code: ${error.extensions.code})`;
              }

              if (error.extensions.errors) {
                context = ` (${error.extensions.errors
                  .map((error) => error.message)
                  .join(', ')})`;
              }
            }

            if (error.path) {
              context = ` (path: ${error.path.join('.')})`;
            }
            return `${error.message}${context}`;
          })
          .join('\n');
      }
    } else {
      errorType = 'Exception';
      errorMessage = exception.message;
    }

    const errorKind = `${eventName} Failed`;

    const error = {
      errorKind,
      errorType,
      errorMessage,
      query: queryDataField,
      pathname,
      ...eventContext,
    };

    const errorEventId = trackError(
      sessionOrSessionData,
      errorKind,
      exception,
      error
    );

    return {
      error: {
        ...error,
        errorEventId,
        fullErrorMessage: makeFullErrorMessage(error, errorEventId),
      },
    };
  }
}

function checkJsonResponse(response: Response): boolean {
  return (
    response?.headers?.get('content-type')?.includes('application/json') ||
    false
  );
}

// https://developer.mozilla.org/en-US/docs/Web/API/fetch#parameters
export function makeFetchOptionsWithBody(
  isJsonRequest,
  sessionId,
  method,
  body
) {
  return {
    method,
    ...(body
      ? {
          ...makeFetchOptions(isJsonRequest, sessionId),
          body: isJsonRequest ? JSON.stringify(body) : body,
        }
      : makeFetchOptions(isJsonRequest, sessionId)),
  };
}

export async function fetchAndParse(
  isJsonRequest: boolean,
  session,
  method: string,
  path: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any
) {
  const url = `${BACKEND_API_URL}/${path}`;
  const fetchOptions = makeFetchOptionsWithBody(
    isJsonRequest,
    session.getIn(['session', 'id']),
    method,
    body
  );

  let error;
  let response: Response;

  try {
    response = await fetch(url, fetchOptions);
  } catch (e) {
    trackError(session, 'Fetch Failed', e, {
      fetchOptions,
    });

    return { exception: e };
  }

  const isJsonResponse = checkJsonResponse(response);

  if (!response.ok) {
    try {
      error = await response.text();
    } catch (e) {
      error = 'HTTP Response Not Ok';
    }

    trackError(session, 'HTTP Response Not Ok', null, {
      fetchOptions,
    });

    return { error, response };
  }

  let data;

  try {
    data = await (isJsonResponse ? response.json() : response.text());
  } catch (e) {
    trackError(session, 'HTTP Response Body Reading Failed', e, {
      isJsonResponse,
      fetchOptions,
    });

    return { exception: e, response };
  }

  return { data, response };
}

export function createStoryFromRawDataset(session, file, metadata) {
  const fileOptions = {
    type: metadata.type,
    lastModified: metadata.lastModified,
  };

  const metadataBlob = new Blob([JSON.stringify(metadata)], {
    type: 'application/json',
  });

  const fileBlob = new File([file], metadata.name, fileOptions);

  const rawDataset = new FormData();
  rawDataset.append('metadata', metadataBlob);
  rawDataset.append('file', fileBlob);

  return fetchAndParse(
    false,
    session,
    'POST',
    'stories/from-raw-dataset',
    rawDataset
  );
}

export function createCheckoutSession(session, body) {
  return fetchAndParse(true, session, 'POST', 'create-checkout-session', body);
}

export function customerPortal(session, body) {
  return fetchAndParse(true, session, 'POST', 'customer-portal', body);
}

export function signOut(session, body) {
  return fetchAndParse(true, session, 'POST', 'auth/signout', body);
}

export function signIn(session, body) {
  return fetchAndParse(true, session, 'POST', 'auth/signin', body);
}

export function me(session) {
  return fetchAndParse(true, session, 'GET', 'me');
}
